import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
    ActionType,
    MarketingGoalTargets,
    RegionId,
    UserId,
    zeroTargets,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { PageHeader, Stage } from "components/layout";
import {
    buildGoalColumns,
    GoalColumn,
    goalColumnBarColor,
    goalColumnKey,
    goalColumnValue,
    referencedActivityTypeIds,
} from "components/marketing-goals/goal-columns";
import { gapColorClass } from "components/marketing-goals/goal-progress-gap";
import { MetricCard } from "components/marketing-goals/metric-card";
import {
    currentYearMonth,
    metricPace,
    monthElapsedFraction,
    ymAddMonths,
    ymLabel,
} from "components/marketing-goals/pace";
import { RecentActivityList } from "components/marketing-goals/recent-activity-list";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptionsState } from "components/tasks/activity-type-options";
import { MultiUserSelect } from "components/ui/multi-user-select";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React, { useMemo, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router";
import {
    useFeature,
    useIsAllowed,
    useNow,
    useRegionId,
    useUsersForRegion,
} from "store/selectors/hooks";
import { EditorMarketer, GoalEditorPanel } from "./marketer-goal-editor";

type RouterOutput = inferRouterOutputs<AppRouter>;
type Attainment = RouterOutput["marketingGoal"]["teamAttainment"][number];
type RecentActivity = RouterOutput["marketingGoal"]["recentActivity"];

const columnActual = (row: Attainment, column: GoalColumn): number =>
    goalColumnValue(row.actuals, column);

const columnTarget = (row: Attainment, column: GoalColumn): number =>
    row.goal ? goalColumnValue(row.goal, column) : 0;

const MetricCell: React.FC<{ actual: number; target: number; frac: number }> = ({
    actual,
    target,
    frac,
}) => {
    if (!target) {
        return <span className="text-sm text-gray-300">—</span>;
    }
    const pace = metricPace(actual, target, frac);
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`size-1.5 rounded-full ${pace.dot}`} title={pace.label} />
            <span className="text-sm tabular-nums">
                <span className="font-medium text-gray-900">{actual}</span>
                <span className="text-gray-400">/{target}</span>
            </span>
        </span>
    );
};

const MonthStepper: React.FC<{
    viewMonth: string;
    options: string[];
    onChange: (month: string) => void;
}> = ({ viewMonth, options, onChange }) => (
    <div className="flex items-center gap-1">
        <SecondaryCompactButton
            aria-label="Previous month"
            onClick={() => onChange(ymAddMonths(viewMonth, -1))}>
            <ChevronLeftIcon className="size-4" />
        </SecondaryCompactButton>
        <Select value={viewMonth} onChange={event => onChange(event.target.value)}>
            {options.map(month => (
                <option key={month} value={month}>
                    {ymLabel(month)}
                </option>
            ))}
        </Select>
        <SecondaryCompactButton
            aria-label="Next month"
            onClick={() => onChange(ymAddMonths(viewMonth, 1))}>
            <ChevronRightIcon className="size-4" />
        </SecondaryCompactButton>
    </div>
);

export const MarketerGoals: React.FC = () => {
    const marketingGoalsEnabled = useFeature("marketingGoals");
    const regionId = useRegionId();
    const effectiveRegionId = regionId ?? ("" as RegionId);
    const canManage = useIsAllowed(ActionType.UpdateRegionSettings);
    const now = useNow();
    const trpcClient = trpc.useTRPC();
    const users = useUsersForRegion(effectiveRegionId);
    const { options: activityTypeOptions, isLoading: optionsLoading } =
        useActivityTypeOptionsState();
    const history = useHistory();
    const location = useLocation();

    const [viewMonth, setViewMonth] = useState<string>(currentYearMonth(now));
    const [marketerFilter, setMarketerFilter] = useState<UserId[]>([]);
    const [editor, setEditor] = useState<{ marketerUserId: UserId | null } | null>(null);

    const selectedMarketerId = (new URLSearchParams(location.search).get("marketer") ??
        null) as UserId | null;
    const selectMarketer = (id: UserId) => {
        const params = new URLSearchParams(location.search);
        params.set("marketer", id);
        history.push(`${location.pathname}?${params.toString()}`);
    };
    const clearMarketer = () => {
        const params = new URLSearchParams(location.search);
        params.delete("marketer");
        const search = params.toString();
        history.push(search ? `${location.pathname}?${search}` : location.pathname);
    };

    const priorMonth = ymAddMonths(viewMonth, -1);
    const frac = monthElapsedFraction(viewMonth, now);

    const teamQuery = useQuery({
        ...trpcClient.marketingGoal.teamAttainment.queryOptions({
            regionId: effectiveRegionId,
            month: viewMonth,
        }),
        enabled: marketingGoalsEnabled && canManage && !!regionId,
    });
    const priorQuery = useQuery({
        ...trpcClient.marketingGoal.teamAttainment.queryOptions({
            regionId: effectiveRegionId,
            month: priorMonth,
        }),
        enabled: marketingGoalsEnabled && canManage && !!regionId,
    });
    const recentQuery = useQuery({
        ...trpcClient.marketingGoal.recentActivity.queryOptions({
            regionId: effectiveRegionId,
            marketerUserId: selectedMarketerId ?? ("" as UserId),
            month: viewMonth,
        }),
        enabled: marketingGoalsEnabled && canManage && !!regionId && !!selectedMarketerId,
    });

    const userName = (id: UserId): string => users[id]?.name || "Unknown user";

    const marketers: EditorMarketer[] = useMemo(
        () =>
            Object.values(users)
                .map(user => ({ id: user.id as UserId, name: user.name }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [users],
    );

    const monthOptions = useMemo(() => {
        const start = ymAddMonths(currentYearMonth(now), -5);
        return Array.from({ length: 7 }, (_, index) => ymAddMonths(start, index));
    }, [now]);

    const rows = useMemo(() => {
        const withGoals = (teamQuery.data ?? []).filter(row => row.goal);
        if (marketerFilter.length === 0) {
            return withGoals;
        }
        return withGoals.filter(row =>
            marketerFilter.includes(row.marketerUserId as UserId),
        );
    }, [teamQuery.data, marketerFilter]);

    const columns = useMemo(() => {
        const goalTargeted = referencedActivityTypeIds(
            rows.flatMap(row => (row.goal ? [row.goal] : [])),
        );
        return buildGoalColumns(
            activityTypeOptions.filter(option => goalTargeted.has(option.id)),
            goalTargeted,
        );
    }, [activityTypeOptions, rows]);

    const priorActualsFor = (id: UserId): MarketingGoalTargets =>
        (priorQuery.data ?? []).find(row => row.marketerUserId === id)?.actuals ??
        zeroTargets();

    if (!marketingGoalsEnabled) {
        return <Redirect to="/reports" />;
    }

    const selectedRow: Attainment | undefined = selectedMarketerId
        ? (teamQuery.data ?? []).find(
              row => row.marketerUserId === selectedMarketerId && row.goal,
          )
        : undefined;

    const handleSaved = (effectiveMonth: string) => {
        setViewMonth(effectiveMonth);
        setEditor(null);
    };

    return (
        <Stage>
            <PageHeader title="Marketer Goal Attainment" stackBreakpoint="md">
                <MonthStepper
                    viewMonth={viewMonth}
                    options={monthOptions}
                    onChange={setViewMonth}
                />
            </PageHeader>
            <div className="min-h-full bg-gray-50 px-4 py-4 lg:px-6">
                {!canManage ? (
                    <div className="py-12 text-center text-sm text-gray-600">
                        You need office administrator access for this office to manage
                        marketer goals.
                    </div>
                ) : teamQuery.isLoading || optionsLoading ? (
                    <div className="py-12 text-center text-gray-500">
                        Loading report...
                    </div>
                ) : teamQuery.isError ? (
                    <div className="py-12 text-center text-red-600">
                        Failed to load report. Please try again.
                    </div>
                ) : selectedRow ? (
                    <MarketerDetail
                        name={userName(selectedRow.marketerUserId as UserId)}
                        row={selectedRow}
                        columns={columns}
                        prior={priorActualsFor(selectedRow.marketerUserId as UserId)}
                        frac={frac}
                        recent={recentQuery.data ?? []}
                        onBack={clearMarketer}
                        onEdit={() =>
                            setEditor({
                                marketerUserId: selectedRow.marketerUserId as UserId,
                            })
                        }
                    />
                ) : (
                    <>
                        <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 pb-4">
                            <div className="w-full sm:w-96">
                                {regionId ? (
                                    <MultiUserSelect
                                        label="Marketer"
                                        placeholder="All marketers"
                                        regionId={regionId}
                                        value={marketerFilter}
                                        onChange={setMarketerFilter}
                                    />
                                ) : null}
                            </div>
                            <PrimaryButton
                                onClick={() => setEditor({ marketerUserId: null })}>
                                New goal
                            </PrimaryButton>
                        </div>

                        {rows.length === 0 ? (
                            <div className="py-12 text-center">
                                <div className="text-base font-semibold text-gray-900">
                                    No goals set for {ymLabel(viewMonth)}
                                </div>
                                <p className="mt-1.5 text-sm text-gray-600">
                                    Create monthly goals for your marketers to start
                                    tracking attainment.
                                </p>
                                <div className="mt-4 flex justify-center">
                                    <PrimaryButton
                                        onClick={() =>
                                            setEditor({ marketerUserId: null })
                                        }>
                                        Create a goal
                                    </PrimaryButton>
                                </div>
                            </div>
                        ) : (
                            <MarketerLeaderboard
                                rows={rows}
                                columns={columns}
                                frac={frac}
                                userName={userName}
                                onSelect={selectMarketer}
                            />
                        )}
                    </>
                )}
            </div>
            {editor && regionId ? (
                <GoalEditorPanel
                    regionId={regionId}
                    fixedMarketerId={editor.marketerUserId}
                    marketers={marketers}
                    now={now}
                    onSaved={handleSaved}
                    onCancel={() => setEditor(null)}
                />
            ) : null}
        </Stage>
    );
};

const MarketerLeaderboard: React.FC<{
    rows: Attainment[];
    columns: GoalColumn[];
    frac: number;
    userName: (id: UserId) => string;
    onSelect: (id: UserId) => void;
}> = ({ rows, columns, frac, userName, onSelect }) => (
    <div className="overflow-x-auto rounded-lg bg-white ring-1 ring-gray-200">
        <table className="min-w-full">
            <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th scope="col" className="px-4 py-3">
                        Marketer
                    </th>
                    {columns.map(column => (
                        <th
                            key={goalColumnKey(column)}
                            scope="col"
                            className="whitespace-nowrap px-4 py-3 text-right">
                            {column.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map(row => (
                    <tr
                        key={row.marketerUserId}
                        role="button"
                        tabIndex={0}
                        onClick={() => onSelect(row.marketerUserId as UserId)}
                        onKeyDown={event => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                onSelect(row.marketerUserId as UserId);
                            }
                        }}
                        className="cursor-pointer border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                            {userName(row.marketerUserId as UserId)}
                        </td>
                        {columns.map(column => (
                            <td
                                key={goalColumnKey(column)}
                                className="whitespace-nowrap px-4 py-3 text-right">
                                <MetricCell
                                    actual={columnActual(row, column)}
                                    target={columnTarget(row, column)}
                                    frac={frac}
                                />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const PriorMonthFooter: React.FC<{ actual: number; prior: number }> = ({
    actual,
    prior,
}) => {
    const delta = actual - prior;
    return (
        <>
            Prior month:{" "}
            <span className="font-semibold tabular-nums text-gray-700">{prior}</span>
            <span className={`ml-1 font-semibold ${gapColorClass(actual, prior)}`}>
                ({delta > 0 ? "+" : ""}
                {delta})
            </span>
        </>
    );
};

const MarketerDetail: React.FC<{
    name: string;
    row: Attainment;
    columns: GoalColumn[];
    prior: MarketingGoalTargets;
    frac: number;
    recent: RecentActivity;
    onBack: () => void;
    onEdit: () => void;
}> = ({ name, row, columns, prior, frac, recent, onBack, onEdit }) => (
    <div className="space-y-5">
        <div className="flex items-center justify-between gap-2">
            <SecondaryCompactButton aria-label="Back to all marketers" onClick={onBack}>
                <ChevronLeftIcon className="size-4" /> All marketers
            </SecondaryCompactButton>
            <div className="ml-auto text-sm font-semibold text-gray-900">{name}</div>
            <SecondaryButton onClick={onEdit}>Edit goals</SecondaryButton>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {columns.map(column => (
                <MetricCard
                    key={goalColumnKey(column)}
                    label={column.label}
                    barColor={goalColumnBarColor(column)}
                    actual={columnActual(row, column)}
                    target={columnTarget(row, column)}
                    frac={frac}
                    footer={
                        <PriorMonthFooter
                            actual={columnActual(row, column)}
                            prior={goalColumnValue(prior, column)}
                        />
                    }
                />
            ))}
        </div>
        <RecentActivityList recent={recent} />
    </div>
);
